import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, BookOpen, Layers, PenSquare, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CARD_TOKENS } from '@/lib/card-tokens';
import type { Course } from '@/types/courses';

interface CourseCardProps {
  course: Course;
  progress: { total: number; done: number; percent: number };
  counts: { modules: number; lessons: number };
  isSubscribed: boolean;
  onSelect: (courseId: string) => void;
  editMode?: boolean;
  onEdit?: (courseId: string) => void;
  onDelete?: (course: Course) => void;
}

const STATUS_BADGE_STYLES: Record<string, string> = {
  Draft: 'bg-slate-400 text-white',
  Archived: 'bg-gray-500 text-white',
  Published: 'bg-green-500 text-white',
  'Coming Soon': 'bg-blue-500 text-white',
};

export const CourseCard = memo(function CourseCard({
  course,
  progress,
  counts,
  isSubscribed,
  onSelect,
  editMode = false,
  onEdit,
  onDelete,
}: CourseCardProps) {
  const isComingSoon = course.status?.toLowerCase() === 'coming soon';
  const description = course.overview || course.tile_description || '';

  const ctaLabel = isComingSoon
    ? 'Coming Soon'
    : !isSubscribed
      ? 'Upgrade to Access'
      : progress.percent === 0
        ? 'Start Course'
        : 'Continue Learning';

  return (
    <Card
      className={cn(
        CARD_TOKENS.base, CARD_TOKENS.hover, CARD_TOKENS.focus,
        'relative flex flex-col cursor-pointer group',
        isComingSoon && 'opacity-70 cursor-default'
      )}
      onClick={() => !isComingSoon && onSelect(course.id)}
    >
      <div className={CARD_TOKENS.shimmer} />
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start mb-1 gap-2 flex-wrap">
          {editMode && course.status && (
            <span
              className={cn(
                'px-2.5 py-0.5 rounded-full text-xs font-medium',
                STATUS_BADGE_STYLES[course.status] ?? 'bg-gray-400 text-white'
              )}
            >
              {course.status}
            </span>
          )}
          {course.is_featured && !isComingSoon && (
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2.5 py-0.5 rounded-full text-xs font-medium">
              Featured
            </span>
          )}
          {isComingSoon && !editMode && (
            <span className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-2.5 py-0.5 rounded-full text-xs font-medium">
              Coming Soon
            </span>
          )}
          {progress.percent > 0 && !isComingSoon && (
            <div className="flex items-center text-xs font-semibold text-green-600 dark:text-green-400 ml-auto">
              <Zap className="w-3 h-3 mr-1 fill-current" />
              in progress
            </div>
          )}
        </div>
        <CardTitle className="text-xl text-gray-900 dark:text-white group-hover:text-[#F37022] transition-colors text-left">
          {course.title}
        </CardTitle>
        {description && (
          <CardDescription className="line-clamp-2 mt-1 text-left">
            {description}
          </CardDescription>
        )}
        {/* Skill domain + lesson type badges */}
        {course.course_type === 'nextgen' && course.lesson_type && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F37022]/10 text-[#F37022]">
              {course.lesson_type}
            </span>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 pb-2">
        {!isComingSoon && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <Layers className="h-4 w-4 mr-1" />
                <span>
                  {counts.modules} {counts.modules === 1 ? 'module' : 'modules'}
                </span>
              </div>
              <div className="flex items-center">
                <BookOpen className="h-4 w-4 mr-1" />
                <span>
                  {counts.lessons} {counts.lessons === 1 ? 'lesson' : 'lessons'}
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500">
                <span>{progress.done}/{progress.total} lessons</span>
                <span>{progress.percent}%</span>
              </div>
              <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#F37022] to-orange-400 rounded-full transition-all"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-4 border-t border-gray-100 dark:border-gray-800">
        {isComingSoon ? (
          <p className="w-full text-center text-sm text-gray-500 dark:text-gray-400 italic">
            This course will be available soon
          </p>
        ) : (
          <Button
            className={cn(
              'w-full transition-colors',
              isSubscribed
                ? 'bg-gray-50 text-gray-900 hover:bg-[#F37022] hover:text-white dark:bg-gray-800 dark:text-white dark:hover:bg-[#F37022] group-hover:bg-[#F37022] group-hover:text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200'
            )}
          >
            {ctaLabel}
          </Button>
        )}
      </CardFooter>

      {editMode && (
        <div
          className={cn(
            'absolute bottom-2 right-2 flex items-center gap-1 z-10',
            'md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity'
          )}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(course.id);
            }}
            className="min-w-[44px] min-h-[44px] md:min-w-[36px] md:min-h-[36px] flex items-center justify-center rounded-md bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:text-[#F37022] hover:border-[#F37022]/40 transition-colors shadow-sm"
            aria-label={`Edit ${course.title}`}
            title="Edit course"
          >
            <PenSquare className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(course);
            }}
            className="min-w-[44px] min-h-[44px] md:min-w-[36px] md:min-h-[36px] flex items-center justify-center rounded-md bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:text-red-600 hover:border-red-400 transition-colors shadow-sm"
            aria-label={`Delete ${course.title}`}
            title="Delete course"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </Card>
  );
});
